
import { useEffect, useRef, useState, memo, useCallback } from 'react';
import { Link } from 'react-router-dom';

const services = [
  {
    icon: 'ri-drop-line',
    title: 'Flood Cleanup',
    link: '/services/flood-cleanup',
    description:
      'When floodwaters invade your property, every minute counts. Our certified technicians respond immediately with industrial-grade extraction equipment and advanced moisture detection technology to remove standing water and prevent structural damage. We meticulously dry affected areas, sanitize surfaces, and restore your space to pre-loss condition—protecting both your property value and your peace of mind.',
  },
  {
    icon: 'ri-water-flash-line',
    title: 'Sewage Cleanup',
    link: '/services/sewage-cleanup',
    description:
      'Sewage backups pose serious health hazards that demand immediate professional intervention. Our specialized team arrives equipped with hospital-grade disinfectants and protective gear to safely extract contaminated water, remove affected materials, and thoroughly sanitize every surface. We follow strict EPA protocols to eliminate harmful pathogens and bacteria, ensuring your property is not just clean, but genuinely safe for your family or employees.',
  },
  {
    icon: 'ri-bug-line',
    title: 'Mold Removal',
    link: '/services/mold-removal',
    description:
      'Mold doesn\'t just damage your property—it threatens your health. Our IICRC-certified mold remediation specialists conduct comprehensive inspections using thermal imaging and air quality testing to identify hidden growth. We contain affected areas, remove contaminated materials, treat surfaces with antimicrobial solutions, and implement moisture control strategies to prevent recurrence. Breathe easier knowing your indoor environment is healthy and mold-free.',
  },
  {
    icon: 'ri-mist-line',
    title: 'Odor Removal',
    link: '/services/odor-removal',
    description:
      'Persistent odors from smoke, mold, sewage, or water damage can make your property uninhabitable and decrease its value. Our advanced odor elimination process goes beyond masking smells—we identify and neutralize odor sources at the molecular level using hydroxyl generators, ozone treatment, and thermal fogging technology. The result is complete odor eradication that restores fresh, clean air to your space.',
  },
  {
    icon: 'ri-fire-line',
    title: 'Smoke Damage',
    link: '/services/smoke-damage',
    description:
      'Fire leaves behind more than visible damage—smoke and soot penetrate deep into walls, fabrics, and HVAC systems. Our fire restoration experts use specialized equipment and techniques to remove acidic soot residue, eliminate smoke odors, and clean or restore affected contents. We work efficiently to prevent permanent staining and corrosion, salvaging as much of your property as possible while ensuring thorough decontamination.',
  },
  {
    icon: 'ri-thunderstorms-line',
    title: 'Storm Damage',
    link: '/services/storm-damage',
    description:
      'Severe weather can compromise your property\'s structural integrity and security within moments. Our emergency response team provides immediate board-up services, emergency roof tarping, and water intrusion prevention to protect your property from further damage. We then coordinate comprehensive repairs—from structural restoration to debris removal—working directly with your insurance company to streamline the recovery process and get you back to normal quickly.',
  },
];

const stages = [
  {
    stage: 'Stage 1',
    title: 'Initial Response',
    description: 'Quick reaction and hard work helps us take care of your disaster FAST! We will make a difficult process easy.',
    icon: 'ri-alarm-warning-line',
  },
  {
    stage: 'Stage 2',
    title: 'Cleanup Process',
    description: 'Our technicians are certified and well trained. We know exactly how to cleanup your property.',
    icon: 'ri-tools-line',
  },
  {
    stage: 'Stage 3',
    title: 'Equipment Monitoring',
    description: 'The drying process depends on the right combination of equipment and technology. We have the tools to get you dried out quick.',
    icon: 'ri-bar-chart-line',
  },
  {
    stage: 'Stage 4',
    title: 'Final Cleanup',
    description: 'Attention to detail is our specialty. We make sure to get you cleaned up and back to normal!',
    icon: 'ri-home-smile-line',
  },
];

const features = [
  { icon: 'ri-timer-flash-line', title: 'Rapid Response', description: 'Available 24/7, 365 days a year' },
  { icon: 'ri-file-list-3-line', title: 'Insurance Assistance', description: 'We work with your insurance company' },
  { icon: 'ri-verified-badge-line', title: 'Licensed & Certified', description: 'IICRC certified technicians' },
];

const ServiceCard = memo(({ service }: { service: typeof services[0] }) => (
  <Link
    to={service.link}
    className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer group block relative overflow-hidden"
  >
    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-sky-100 rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-sky-600 transition-colors">
      <i className={`${service.icon} text-2xl sm:text-3xl text-sky-600 group-hover:text-white transition-colors`}></i>
    </div>
    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{service.title}</h3>
    <p className="text-gray-600 text-lg leading-relaxed mb-12">{service.description}</p>
    <div className="absolute bottom-6 right-6">
      <div className="bg-white border-2 border-sky-600 text-sky-600 px-4 py-2 rounded-full flex items-center gap-2 shadow-md group-hover:bg-sky-600 group-hover:text-white transition-all">
        <span className="text-sm font-semibold whitespace-nowrap">Learn More</span>
        <i className="ri-arrow-right-line text-base"></i>
      </div>
    </div>
  </Link>
));

ServiceCard.displayName = 'ServiceCard';

const StageCard = memo(({ stage }: { stage: typeof stages[0] }) => (
  <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center hover:bg-white/20 transition-all">
    <div className="w-11 h-11 sm:w-14 sm:h-14 bg-sky-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
      <i className={`${stage.icon} text-xl sm:text-2xl text-white`}></i>
    </div>
    <p className="text-sky-400 text-lg sm:text-xl font-semibold mb-1 sm:mb-2">{stage.stage}</p>
    <h4 className="text-base sm:text-xl font-bold text-white mb-2 sm:mb-3 whitespace-nowrap">{stage.title}</h4>
    <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{stage.description}</p>
  </div>
));

StageCard.displayName = 'StageCard';

const FeatureCard = memo(({ icon, title, description }: { icon: string; title: string; description: string }) => (
  <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow text-center">
    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
      <i className={`${icon} text-2xl sm:text-3xl text-sky-600`}></i>
    </div>
    <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{title}</h4>
    <p className="text-gray-600 text-lg">{description}</p>
  </div>
));

FeatureCard.displayName = 'FeatureCard';

function Services() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const scrollToContact = useCallback(() => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => {
      (document.getElementById('name') as HTMLInputElement)?.focus();
    }, 500);
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-16 sm:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center mb-10 sm:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-sky-600 text-xl font-semibold mb-2">Comprehensive Solutions</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Disaster Cleanup Services
          </h2>
          <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
            From water damage to fire restoration, we provide complete disaster cleanup services to make
            your loss look like it never happened.
          </p>
        </div>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-14 sm:mb-20 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} />
          ))}
        </div>

        <div
          className={`bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
              The Stages of Cleanup
            </h3>
            <p className="text-gray-300 text-base sm:text-lg max-w-2xl mx-auto">
              Our proven 4-stage process ensures thorough restoration and gets you back to normal as
              quickly as possible.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {stages.map((stage, index) => (
              <StageCard key={index} stage={stage} />
            ))}
          </div>
        </div>

        <div
          className={`mt-10 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>

        <div className="mt-10 sm:mt-16 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
          <button
            onClick={scrollToContact}
            className="w-full sm:w-auto bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 hover:brightness-90 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Get Your Free Assessment
          </button>
          <a
            href="tel:3853406632"
            className="w-full sm:w-auto text-center border border-gray-300 text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 hover:brightness-90 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Call Now: (385) 340-6632
          </a>
        </div>
      </div>
    </section>
  );
}

export default memo(Services);
